<template>
  <footer class="site-footer">
    <div class="inner">
      <div class="langswitch _hidden-vl">
        <LangSwitch :text-class="'text-3xs-vl text-3xs-vs'" />
      </div>
      <div v-if="siteInfo.address" class="address">
        <StructuredText
          v-if="siteInfo.address"
          :data="siteInfo.address"
          class="text structured-text text-sm"
        />
        <Directions class="text-xs" variant="underline" />
      </div>
      <div class="contact">
        <a
          v-if="siteInfo.telephone"
          class="text-2xl link"
          :href="`tel:${siteInfo.telephone}`"
          >{{ siteInfo.telephone }}</a
        >
        <a
          v-if="siteInfo.email"
          class="text-sm link email"
          :href="`mailto:${siteInfo.email}`"
          >{{ siteInfo.email }}</a
        >
      </div>
      <div class="links" v-if="footerNavigation">
        <div class="links-inner">
          <div
            v-for="item in footerNavigation.navLinks"
            :key="item.id"
            class="item"
          >
            <NavLink class="link text-3xs-vl text-2xs-vs" :data="item" />
          </div>
        </div>
        <div class="langswitch _hidden-vs">
          <LangSwitch :text-class="'text-3xs-vl text-3xs-vs'" />
        </div>
      </div>
      <div class="footnote text-3xs-vl text-2xs-vs">
        <span>
          &copy; {{ currentYear }} {{ siteInfo.copyrightInformation }}
        </span>
        <a class="gridonic" href="https://gridonic.ch/" target="_blank">{{
          $t('gridonicText')
        }}</a>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { StructuredText } from 'vue-datocms';
import LangSwitch from '../site-header/LangSwitch.vue';
import NavLink from '~/components/site-header/NavLink.vue';
import Directions from '~/components/partials/Directions.vue';
import type { SiteInfoFragment } from '#gql';

const siteInfo = useNuxtApp().$siteInfo as SiteInfoFragment;

const { localeProperties } = useI18n();

const { data } = await useAsyncGql({
  operation: 'GetFooterNavigation',
  variables: {
    locale: localeProperties.value.siteLocale,
  },
});
const footerNavigation = data.value.footerNavigation;

const currentYear = new Date().getFullYear();
</script>

<style scoped lang="scss">
.site-footer {
  position: relative;
  padding: var(--base-component-padding-block)
    var(--base-component-padding-inline);
  background-color: var(--color-aqua-200-20);
  margin-top: 2.5rem;

  > .inner {
    display: grid;
    column-gap: 2rem;
    row-gap: 6rem;
    justify-content: space-between;

    @media (--vs) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
    @media (--vl) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .address {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .directions {
        display: flex;
        gap: 0.5rem;
      }
    }

    > .navigation,
    > .address,
    > .contact {
      align-self: flex-start;
    }

    > .contact {
      > .link {
        display: block;
        text-decoration: none;

        position: relative;
        width: fit-content;

        &.email::after,
        &.email::before {
          content: '';
          position: absolute;
          width: 100%;
          left: 0;
          bottom: 0;
          height: 3px;
          background-color: rgba(9, 89, 139, 0.2);
          transition: transform 0.3s ease-in-out;
        }
        &.email::after {
          transform: scaleX(0);
          transform-origin: left;
          background-color: var(--color-blue-500);
        }

        &:hover {
          &.email::after {
            transform: scaleX(1);
          }
        }
      }
    }

    .links {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 1rem;
      height: 100%;
      margin-bottom: auto;

      .links-inner {
        display: grid;
        justify-content: end;
        gap: 1rem;
      }

      .langswitch {
        justify-self: flex-end;
        width: fit-content;
        margin-left: auto;
      }

      .item {
        height: auto;
        display: flex;
        justify-content: flex-end;

        .link {
          position: relative;

          &:hover {
            cursor: pointer;
          }

          &::after {
            content: '';
            position: absolute;
            left: 0;
            bottom: -0.25rem;
            width: 100%;
            background-color: currentColor;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.3s ease-in-out;
            height: 2px;
          }

          &:hover::after {
            transform: scaleX(1);
          }
        }
      }
    }
  }
  .footnote {
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    grid-column: -1/1;
    span {
      width: fit-content;
    }
    .gridonic {
      text-decoration: none;
    }
  }
}

@media (--vs) {
  .site-footer {
    padding: 2.5rem var(--base-component-padding-inline);

    > .inner {
      row-gap: 2.5rem;
      .contact {
        align-self: end;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
      .links {
        justify-content: start;

        .links-inner {
          display: grid;
          justify-content: start;
          gap: 0.25rem;

          .item {
            justify-content: start;
          }
        }
      }
      .footnote {
        flex-direction: column;
        max-width: 75%;
        gap: 1.5rem;
      }
    }
  }
}
</style>
